import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Link } from '@mui/material';
import { useParams } from 'react-router';
import { PageContainer } from '../../components/page-container';
import { DelayedLoadIndicator } from '../../components/delayed-load-indicator';
import { UserSettingsSidebar } from './settings/user-settings-sidebar';
import { UserSettingsMobileNav } from './settings/user-settings-mobile-nav';
import { useActiveSettingsTab } from './settings/settings-tabs';
import { UserSettingsTokens } from './tokens/user-settings-tokens';
import { UserSettingsTrustedPublishers } from './trusted-publishing/user-settings-trusted-publishers';
import { useTrustedPublishingStatus } from './trusted-publishing/use-trusted-publishers';
import { UserSettingsProfile } from './profile/user-settings-profile';
import { UserSettingsNamespaces } from './namespaces/user-settings-namespaces';
import { UserSettingsExtensions } from './extensions/user-settings-extensions';
import { UserSettingsCustomers } from './customers/user-settings-customers';
import { MainContext } from '../../context';
import { LoginComponent } from '../../default/login';
import { ExtensionSettings } from './extensions/extension-settings';
export const UserSettings = props => {
    const { pageSettings, user, loginProviders } = useContext(MainContext);
    const { tab, namespace, extension } = useParams();
    const activeTab = useActiveSettingsTab();
    const { data: trustedPublishingStatus } = useTrustedPublishingStatus();
    const trustedPublishingEnabled = trustedPublishingStatus?.enabled ?? false;
    const renderTab = (user) => {
        if (tab == null && namespace != null && extension != null) {
            return _jsx(ExtensionSettings, { namespace: namespace, extension: extension });
        }
        switch (activeTab) {
            case 'profile':
                return _jsx(UserSettingsProfile, { user: user });
            case 'tokens':
                return _jsx(UserSettingsTokens, {});
            case 'trusted-publishers':
                return trustedPublishingEnabled ? _jsx(UserSettingsTrustedPublishers, {}) : null;
            case 'namespaces':
                return _jsx(UserSettingsNamespaces, { selectedName: namespace });
            case 'extensions':
                return _jsx(UserSettingsExtensions, {});
            case 'customers':
                return _jsx(UserSettingsCustomers, {});
            default:
                return null;
        }
    };
    const renderContent = () => {
        if (props.userLoading) {
            return _jsx(DelayedLoadIndicator, { loading: true });
        }
        if (!user) {
            return loginProviders ? (_jsxs(PageContainer, { children: [_jsx(Typography, { variant: 'h4', children: "Not Logged In" }), _jsx(Box, { mt: 2, children: _jsxs(Typography, { variant: 'body1', children: ["Please", ' ', _jsx(LoginComponent, { loginProviders: loginProviders, renderButton: (href, onClick) => {
                                        return (_jsx(Link, { color: 'secondary', href: href, onClick: onClick, children: "log in" }));
                                    } }), ' ', "to access your account settings."] }) })] })) : null;
        }
        return (_jsxs(PageContainer, { sx: {
                // No mobile padding: the sticky tab pills sit directly below the navbar.
                pt: { xs: 0, md: '2.625rem' },
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '14.75rem minmax(0, 1fr)' },
                gap: { xs: '1.5rem', md: '2.5rem' },
                alignItems: 'start'
            }, children: [_jsx(UserSettingsSidebar, {}), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(UserSettingsMobileNav, {}), renderTab(user)] })] }));
    };
    return (_jsxs(_Fragment, { children: [_jsx(Helmet, { children: _jsxs("title", { children: ["Settings \u2013 ", pageSettings.pageTitle] }) }), renderContent()] }));
};
//# sourceMappingURL=user-settings.js.map